import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import useTournamentRole from '../../hooks/useTournamentRole';
import { DASHBOARD_STACK } from '../../utils/screenNames';

// Same helper used by PaymentsList — kept inline to avoid coupling the
// dashboard to the payments page module.
const getCurrencySymbol = (currency) => {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(0)
      .replace(/\d/g, '')
      .trim();
  } catch {
    return currency;
  }
};

const SummaryCard = ({ symbol, amount, label }) => (
  <div className="bg-app-surface rounded-2xl px-5 py-5">
    <p className="text-[#FEFCEB] text-[25px] font-semibold m-0">
      {symbol} {Number(amount || 0).toLocaleString()}
    </p>
    <p className="text-app-text-secondary text-[13px] mt-1 m-0">{label}</p>
  </div>
);

const DashboardPaymentSummary = () => {
  const navigate = useNavigate();
  const { permissions } = useTournamentRole();
  const { selectedTournament, dashboard } = useSelector((state) => state.tournament);

  // Backend rejects non-organisers with 403 — keep the dashboard summary in
  // sync with the Payments page's visibility rules.
  if (!permissions.canManagePayments) return null;

  const tournamentId = selectedTournament?.id || selectedTournament?._id;
  const isFresh = dashboard && dashboard.tournamentId === tournamentId;
  const summary = isFresh ? dashboard.paymentSummary : null;
  const currency = summary?.currency || 'GBP';
  const symbol = getCurrencySymbol(currency);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-app-text text-[18px] font-medium m-0">Payment status</h3>
        <button
          type="button"
          onClick={() => navigate(DASHBOARD_STACK.PAYMENTS)}
          className="text-app-text-secondary text-[13px] underline hover:text-app-text transition-colors cursor-pointer"
        >
          See more
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard symbol={symbol} amount={summary?.totalReceived} label="Total received" />
        <SummaryCard symbol={symbol} amount={summary?.totalPending} label="Total pending" />
        <SummaryCard symbol={symbol} amount={summary?.totalOverdue} label="Total overdue" />
      </div>
    </div>
  );
};

export default DashboardPaymentSummary;
